import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import curbsideAPIService from '../services/curbsideAPIService';
import { config } from '../config';

// Thunk to send notifications
export const sendNotificationSettings = createAsyncThunk(
  'notificationSettings/sendNotificationSettings',
  async (
    {
      phoneNumber,
      selectedOption,
      autoSend,
      storeOpenDelayMinutes,
      fulfillmentDelayMinutes,
      storeIds,
    }: {
      phoneNumber: string;
      selectedOption: string | null;
      autoSend: boolean;
      storeOpenDelayMinutes: number;
      fulfillmentDelayMinutes: number;
      storeIds: string[];
    },
    { rejectWithValue, dispatch }
  ) => { 
    const endpoint = 'chainsetting';
    const data = {
      chainSetting: {
        ChainSettingsId: config.chainSettingId,
        storePhoneNumbers: phoneNumber,
        whenToSendSMS: selectedOption ?? null,
        autoSendReadyForPickupNotifications: autoSend,
        minutesBeforeStoreOpening: storeOpenDelayMinutes,
        minutesBeforeFulfillmentSlot: fulfillmentDelayMinutes,
        storeIds: storeIds,
      },
    };

    try {
      const response = await curbsideAPIService.post(endpoint, data);
      toast.success('Setting saved successfully');

      dispatch(fetchChainSetting(storeIds));

      return response;
    } catch (error) {
      console.error('Error saving notification settings:', error);
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchChainSetting = createAsyncThunk(
  'chainSettings/fetchChainSetting',
  async (storeIds: string[], { rejectWithValue }) => {
    const endpoint = `chainsetting/${config.chainSettingId}`;
    const data = { storeId: storeIds[0] || [] };

    try {
      const response = await curbsideAPIService.get(endpoint, data);
      return response;
    } catch (error) {
      console.error('Error fetching chain settings data:', error);
      return rejectWithValue((error as any).response ?? 'Failed to fetch chain settings');
    }
  }
);

const notificationSettingsSlice = createSlice({
  name: 'notificationSettings',
  initialState: {
    phoneNumber: '',
    selectedOption: '',
    autoSend: false,
    storeOpenDelayMinutes: 0,
    fulfillmentDelayMinutes: 0,
    SelectedstoreIds: [],
    status: 'idle',
    error: null as string | null,
  },
  reducers: {
    setPhoneNumber: (state, action) => {
      return {
        ...state,
        phoneNumber: action.payload,
      };
    },
    setSelectedOption: (state, action) => {
      return {
        ...state,
        selectedOption: action.payload,
      };
    },
    setStoreIds: (state, action) => {
      return {
        ...state,
        SelectedstoreIds: action.payload,
      };
    },
    toggleAutoSend: (state) => {
      return {
        ...state,
        autoSend: !state.autoSend,
      };
    },
    setStoreOpenDelayMinutes: (state, action) => {
      return {
        ...state,
        storeOpenDelayMinutes: action.payload,
      };
    },
    setFulfillmentDelayMinutes: (state, action) => {
      return {
        ...state,
        fulfillmentDelayMinutes: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendNotificationSettings.pending, (state) => {
        return {
          ...state,
          status: 'loading',
        };
      })
      .addCase(sendNotificationSettings.fulfilled, (state) => {
        return {
          ...state,
          status: 'succeeded',
          selectedOption: '',
          phoneNumber: '',
          autoSend: false,
        };
      })
      .addCase(sendNotificationSettings.rejected, (state, action) => {
        return {
          ...state,
          status: 'failed',
          error: action.payload as string,
        };
      })
      .addCase(fetchChainSetting.pending, (state) => {
        return {
          ...state,
          loading: true,
          error: null,
        };
      })
      .addCase(fetchChainSetting.fulfilled, (state, action) => {
        return {
          ...state,
          loading: false,
          phoneNumber: action.payload.storePhoneNumbers,
          selectedOption: action.payload.whenToSendSMS,
          autoSend: action.payload.autoSendReadyForPickupNotifications,
          storeOpenDelayMinutes: action.payload.minutesBeforeStoreOpening,
          fulfillmentDelayMinutes: action.payload.minutesBeforeFulfillmentSlot,
          storeIds: action.payload?.storeIds ?? [],
        };
      })
      .addCase(fetchChainSetting.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          error: action.payload as string | null,
        };
      });
  },
});

export const {
  setPhoneNumber,
  setSelectedOption,
  toggleAutoSend,
  setStoreOpenDelayMinutes,
  setFulfillmentDelayMinutes,
  setStoreIds,
} = notificationSettingsSlice.actions;

export default notificationSettingsSlice.reducer;
